import { InputRadio } from '@/components/InputRadio/InputRadio'
import { InputSelect } from '@/components/InputSelect/InputSelect'
import { UseRegistrationAddressField } from '@/components/PriceCalculator/UseRegistrationAddressField'
import { StepperInput } from '@/components/StepperInput/StepperInput'
import type { InputField as InputFieldType } from '@/services/PriceCalculator/Field.types'
import { convertToDate } from '@/utils/date'
import { TextField } from 'ui/src/components/TextField/TextField'
import { InputDay } from '../InputDay/InputDay'
import { CurrentInsuranceField } from './CurrentInsuranceField/CurrentInsuranceField'
import { CurrentInsuranceFieldReadOnly } from './CurrentInsuranceField/CurrentInsuranceFieldReadOnly'
import { CarMileageField } from './CarMileageField'
import { CarRegistrationNumberField } from './CarRegistrationField'
import { ExtraBuildingsField } from './ExtraBuildingsField'
import { MaskedAddressAutoCompleteField } from './MaskedAddressAutoCompleteField'
import { PetBreedsField } from './PetBreedsField'
import { useTranslateFieldLabel } from './useTranslateFieldLabel'

type Props = {
  field: InputFieldType
  autoFocus?: boolean
}

export const AutomaticField = ({ field, autoFocus }: Props) => {
  const translateLabel = useTranslateFieldLabel()

  switch (field.type) {
    case 'text':
      return (
        <TextField
          type={field.inputType ?? 'text'}
          name={field.name}
          label={translateLabel(field.label)}
          pattern={field.pattern}
          minLength={field.minLength}
          maxLength={field.maxLength}
          inputMode={field.inputMode ?? 'text'}
          required={field.required}
          defaultValue={field.defaultValue}
          autoFocus={autoFocus}
        />
      )

    case 'number':
      return (
        <TextField
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          name={field.name}
          label={translateLabel(field.label)}
          min={field.min}
          max={field.max}
          required={field.required}
          defaultValue={field.defaultValue}
          autoFocus={autoFocus}
          suffix={field.suffix}
        />
      )

    case 'date':
      return (
        <InputDay
          name={field.name}
          label={translateLabel(field.label)}
          required={field.required}
          defaultSelected={convertToDate(field.defaultValue) ?? undefined}
          fromDate={field.min === 'TODAY' ? new Date() : (convertToDate(field.min) ?? undefined)}
          toDate={field.max === 'TODAY' ? new Date() : (convertToDate(field.max) ?? undefined)}
          autoFocus={autoFocus}
        />
      )

    case 'radio': {
      return (
        <InputRadio
          name={field.name}
          label={translateLabel(field.label)}
          displayLabel={field.displayLabel}
          required={field.required}
          defaultValue={field.defaultValue}
          orientation={field.stacking}
          options={field.options.map((option, index) => ({
            label: translateLabel(option.label),
            value: option.value,
            autoFocus: autoFocus && index === 0,
          }))}
        />
      )
    }

    case 'select':
      return (
        <InputSelect
          name={field.name}
          label={translateLabel(field.label)}
          required={field.required}
          defaultValue={field.defaultValue}
          options={field.options.map((option) => ({
            ...option,
            name: translateLabel(option.label),
          }))}
          autoFocus={autoFocus}
        />
      )

    case 'use-registration-address':
      return <UseRegistrationAddressField label={translateLabel(field.label)} />

    case 'extra-buildings':
      return (
        <ExtraBuildingsField
          field={field}
          buildingOptions={field.buildingOptions.map((buildingOption) => ({
            ...buildingOption,
            name: buildingOption.label,
          }))}
        />
      )

    case 'car-registration-number':
      return <CarRegistrationNumberField field={field} autoFocus={autoFocus} />
    case 'car-mileage':
      return <CarMileageField field={field} />

    case 'current-insurance':
      return <CurrentInsuranceField name={field.name} label={translateLabel(field.label)} />

    case 'current-insurance-readonly':
      return <CurrentInsuranceFieldReadOnly />

    case 'masked-address':
      return <MaskedAddressAutoCompleteField field={field} label={translateLabel(field.label)} />

    case 'stepper':
      return (
        <StepperInput
          name={field.name}
          max={field.max}
          min={field.min}
          required={field.required}
          defaultValue={field.defaultValue}
          autoFocus={autoFocus}
          label={translateLabel(field.label)}
          optionLabel={(count) => translateLabel(field.valueLabel, { count })}
        />
      )

    case 'pet-cat-breeds':
    case 'pet-dog-breeds':
      return <PetBreedsField field={field} />

    default: {
      const badField: unknown = field
      console.warn(`Did not find field type=${(badField as { type: string }).type}.  Field not displayed`)
      return null
    }
  }
}
